/**
 * The tutorial's kitchen, running.
 *
 * This is the whole of the interactive tutorial's logic, and it is a pure
 * function of (sandbox, actions) — no canvas, no React, no timer, no browser.
 * `Tutorial.tsx` owns the clock, the keys and the paint; this owns the rules,
 * which is the half worth asserting on. The split is the one `micCheckWindow.ts`
 * and `latency.ts` already make for the other two gates.
 *
 * The rules are not a second implementation of the game. `tick` here is
 * `@overcooked/engine`'s `tick` — the same fold the server runs fifty times a
 * second, over the same `GameState`, producing the same `GameEvent`s. That is
 * the point of the sandbox: what the player practises *is* the game, so a rule
 * that changes changes here too, and there is no tutorial that quietly teaches
 * last month's kitchen.
 *
 * Two things this file reproduces from the server's transport rather than
 * importing, because they are transport state and not game state:
 *
 *   - a held interact key becomes one synthetic `interact_hold` per tick
 *     (`transport/loop.ts`'s `withHolds`), so a chop bar fills while the key is
 *     down and freezes where it got to when it is let go;
 *   - the press edge always beats the synthesised hold, so the section 5
 *     dispatch runs exactly once per press.
 *
 * A step advances when the engine emits the event that step is teaching. Not
 * when a button is pressed, and not on a timer: the player chopped something,
 * or they did not.
 */

import type {
  Direction,
  GameEvent,
  IngredientId,
  PlayerAction,
  PlayerId,
  PlayerInput,
  StationType,
} from '@overcooked/shared'
import { cellKey } from '@overcooked/shared'
import type { Content, GameState } from '@overcooked/engine'
import { cloneState, equipmentForRack, initialState, serialise, tick } from '@overcooked/engine'
import { KEY_TO_DIRECTION } from './keys'
import { PRACTICE_CONTENT, TUTORIAL_CONTENT, TUTORIAL_MAP_NAME } from './tutorialMap'

/** One seat, and it is yours. Named rather than a UUID so a test can read it. */
export const SANDBOX_PLAYER_ID: PlayerId = 'chef'

/**
 * Which of the two kitchens this sandbox is running.
 *
 * `teaching` is the step-driven lesson: learner's clocks, one ticket that
 * cannot lapse, a fire lit for you at the moment it is being explained, and
 * fixtures that arrive one at a time. `practice` is the same kitchen with the
 * shipped tuning and none of the scaffolding — the whole room from tick zero, a
 * five-minute clock, tickets arriving on the real cadence and expiring on their
 * own, and a pan nobody is going to light for you.
 *
 * It is a mode rather than a second module because it is not a second game.
 * Everything below reads `contentFor(mode)` and is otherwise identical, which
 * is the claim the practice round is making: what you just learned is what you
 * are about to play.
 */
export type SandboxMode = 'teaching' | 'practice'

const contentFor = (mode: SandboxMode): Content =>
  mode === 'practice' ? PRACTICE_CONTENT : TUTORIAL_CONTENT

/** What the player must do, and what the kitchen looks like when they start. */
export type TutorialGoal = (event: GameEvent, state: GameState) => boolean
export type TutorialSetup = (state: GameState) => GameState

/**
 * The half of a `TutorialStep` the sandbox reads. Taking the narrow shape rather
 * than the whole step keeps this module free of anything to do with drawing.
 */
export interface SandboxStep {
  id: string
  goal?: TutorialGoal
  setup?: TutorialSetup
  /** Leave the correctly resolved state on screen until the learner chooses Next. */
  pauseAfterGoal?: boolean
  /** Starts this step with a clean tutorial kitchen. */
  fresh?: boolean
  /**
   * Fixtures this step brings into the kitchen, and which are absent from every
   * step before it. One object at a time is the whole idea: a learner reading
   * "walk, and you are facing it" should not be looking at a bin and a fire
   * extinguisher they have been told nothing about.
   */
  reveals?: StationType[]
  /** Ingredient crates enabled from this step onward. */
  unlocks?: IngredientId[]
}

/**
 * Which fixtures the tutorial is allowed to hold back.
 *
 * Only the two the lesson introduces late. Everything else is either walked on,
 * needed from the first step, or would leave a hole in the room — hiding the
 * hatch would take the player's reason for cooking away with it.
 */
export const HIDEABLE_STATIONS: readonly StationType[] = ['bin', 'extinguisher_rack']

/**
 * The kitchen with the not-yet-taught fixtures taken out of it.
 *
 * "Taken out" is `wall`, not a deletion and not a flag on `Station`: the two
 * hideable fixtures both stand in the map's perimeter, so a wall is what is
 * behind them, and a station the engine sees as a wall is one nothing can be
 * done at and nothing draws a fixture for. The map is the authority on the way
 * back, which is what lets a reveal be a plain restore rather than a saved copy
 * of something that might since have changed.
 *
 * Pure, like every other staging function here: it clones and returns.
 */
export function applyVisibility(
  state: GameState,
  revealed: ReadonlySet<StationType>,
): GameState {
  const next = cloneState(state)
  for (const tile of Object.values(TUTORIAL_CONTENT.map.tiles)) {
    if (!HIDEABLE_STATIONS.includes(tile.type)) continue
    const station = next.stations[cellKey(tile.cell)]
    if (!station) continue
    const shouldShow = revealed.has(tile.type)
    if (shouldShow === (station.type === tile.type)) continue
    if (!shouldShow) {
      station.type = 'wall'
      station.item = null
      station.container = null
      station.heldBy = null
      station.resumedAtTick = null
      continue
    }
    // Back as it left the map: a rack holds the thing it is a rack for, which
    // is the whole of "the extinguisher is somewhere to be picked up".
    const rackItem = equipmentForRack(TUTORIAL_CONTENT, tile.type)
    station.type = tile.type
    station.item = rackItem ? { type: 'equipment', id: rackItem } : null
    station.dispenses = tile.dispenses
  }
  return next
}

export interface Sandbox {
  state: GameState
  /** Which step is being taught. Always 0 in `practice`, which has no steps. */
  index: number
  /** Events from the tick that produced this sandbox — what the feed renders. */
  events: GameEvent[]
  /** Is the interact key down? Transport state, exactly as on the server. */
  held: boolean
  /** True once the last step's goal has fired, or the practice clock has run out. */
  finished: boolean
  /** Which kitchen and which clocks. */
  mode: SandboxMode
  /**
   * How the practice round went, once it is over.
   *
   * `null` for the whole of the teaching tutorial: a lesson has no score and no
   * end. In `practice` this is set on the tick the round clock runs out, at the
   * same moment `ROUND_ENDED` is put on the stream — the two are one fact, said
   * once to the component and once to the renderer.
   */
  ended: { reason: 'timer'; score: number } | null
}

/** A fresh kitchen, staged for the step the player is starting on. */
export function createSandbox(
  steps: readonly SandboxStep[],
  index = 0,
  mode: SandboxMode = 'teaching',
): Sandbox {
  const content = contentFor(mode)
  const base = initialState(content, [SANDBOX_PLAYER_ID], TUTORIAL_MAP_NAME)
  return {
    // Practice is the whole room from the first tick. Holding a fixture back
    // there would be the round hiding a station the player is being scored on.
    state: mode === 'practice' ? applyVisibility(base, new Set(HIDEABLE_STATIONS)) : stage(base, steps, index),
    index: mode === 'practice' ? 0 : index,
    events: [],
    held: false,
    finished: false,
    mode,
    ended: null,
  }
}

/**
 * The round opener, so the practice round's clock is anchored like a real one.
 *
 * `TickClock` reads `startedAtEpochMs` off this event and every bar, fuse and
 * the round clock itself are drawn against it from there on. Without it the
 * feed falls back to extrapolating from the last frame that happened to arrive,
 * which is exactly the bug phase 1 removed — and a practice round is the one
 * place in the client that can produce this event for itself.
 */
export function practiceRoundStarted(state: GameState, startedAtEpochMs: number): GameEvent {
  return {
    tick: state.tick,
    wallClock: startedAtEpochMs,
    actor: 'system',
    type: 'ROUND_STARTED',
    mapName: state.mapName,
    players: serialise(state).players,
    durationTicks: state.roundDurationTicks,
    startedAtEpochMs,
  }
}

/**
 * One tick.
 *
 * `actions` are the edges the player produced since the last tick — a move, a
 * press, a release. The hold is synthesised here rather than sent, so the bar's
 * smoothness never depends on how often keys repeat.
 */
export function advanceSandbox(
  sandbox: Sandbox,
  steps: readonly SandboxStep[],
  actions: readonly PlayerAction[],
): Sandbox {
  // The pass is closed. The server's loop stops simulating at the boundary
  // rather than freezing the last frame, and so does this: a kitchen that kept
  // cooking under a scoreboard would be a round that did not really end.
  if (sandbox.ended) return { ...sandbox, events: [] }

  let held = sandbox.held
  const inputs: PlayerInput[] = []
  let pressed = false

  for (const action of actions) {
    if (action.kind === 'interact_release') {
      held = false
      continue
    }
    if (action.kind === 'interact_hold') continue // never a player's to send
    if (action.kind === 'interact') {
      held = true
      pressed = true
    }
    inputs.push({ playerId: SANDBOX_PLAYER_ID, action })
  }

  if (held && !pressed) inputs.push({ playerId: SANDBOX_PLAYER_ID, action: { kind: 'interact_hold' } })

  const [state, events] = tick(sandbox.state, inputs, contentFor(sandbox.mode))

  // The practice round's own whistle. `ROUND_ENDED` is the transport's event
  // rather than the engine's — the server emits it from `endRound`, at the
  // boundary its loop watches for — so the sandbox, which is its own transport,
  // emits it at the same boundary for the same reason: the feed learns a round
  // is over from the stream, never by watching the tick.
  if (sandbox.mode === 'practice' && state.tick >= state.roundDurationTicks) {
    const ended = { reason: 'timer' as const, score: state.score }
    return {
      ...sandbox,
      state,
      events: [...events, { tick: state.tick, wallClock: 0, actor: 'system', type: 'ROUND_ENDED', ...ended }],
      held,
      finished: true,
      ended,
    }
  }

  const step = steps[sandbox.index]
  const satisfied = step?.goal !== undefined && events.some((event) => step.goal?.(event, state))
  if (!satisfied) return { ...sandbox, state, events, held }

  // Most lessons roll directly into their next handoff. A few deliberately do
  // not: clearing a burnt pan leaves it safe, so the fire drill must be an
  // explicit separate scenario rather than an implied consequence of doing the
  // right thing. Keep that resolved engine state visible until Next is chosen.
  if (step.pauseAfterGoal) return { ...sandbox, state, events, held, finished: true }

  const next = sandbox.index + 1
  if (next >= steps.length) {
    return { ...sandbox, state, index: sandbox.index, events, held, finished: true }
  }
  return { ...sandbox, state: stage(state, steps, next), index: next, events, held, finished: false }
}

/**
 * Jump to a step because the player asked to — Back, Next, or a step with
 * nothing to do in it. The setup runs either way, so a step always finds the
 * kitchen it was written for, however the player arrived at it. Navigation is
 * a reset rather than a rewind over the current state: Back and Next are for
 * repeating or previewing a lesson, not for carrying a half-chopped ingredient
 * or a held pan into it.
 */
export function goToStep(sandbox: Sandbox, steps: readonly SandboxStep[], index: number): Sandbox {
  const clamped = Math.max(0, Math.min(index, steps.length - 1))
  return createSandbox(steps, clamped, sandbox.mode)
}

/**
 * The kitchen this step is taught in: what has been revealed by now, and then
 * the step's own `setup`.
 *
 * Visibility is recomputed from the whole prefix rather than accumulated, for
 * the reason the manifest is derived rather than counted — a running total is a
 * second thing that can disagree. It also makes arriving at a step directly
 * (`createSandbox(steps, 4)`, or a replay of the tutorial) land in exactly the
 * kitchen walking there would have produced.
 *
 * Visibility first, `setup` second: `lightTheStove` stocks the extinguisher
 * rack, and it can only do that once the rack is back in the room.
 */
function stage(state: GameState, steps: readonly SandboxStep[], index: number): GameState {
  const revealed = new Set<StationType>()
  const unlocked = new Set<IngredientId>()
  for (let i = 0; i <= index && i < steps.length; i += 1) {
    for (const type of steps[i]?.reveals ?? []) revealed.add(type)
    for (const ingredient of steps[i]?.unlocks ?? []) unlocked.add(ingredient)
  }
  const step = steps[index]
  const base = step?.fresh ? initialState(TUTORIAL_CONTENT, [SANDBOX_PLAYER_ID], TUTORIAL_MAP_NAME) : state
  const shown = unlocked.has('carrot')
    ? applyVisibility(base, revealed)
    : hideTutorialCarrotCrate(applyVisibility(base, revealed))
  return step?.setup?.(shown) ?? shown
}

/**
 * The guided path teaches the meat route first. Leaving the carrot crate live
 * lets a learner make a valid-but-wrong first choice, then blocks the next
 * card until they discover why. It is a practice-round option, not a tutorial
 * option, so the staged teaching kitchen turns that one crate back into a wall.
 */
function hideTutorialCarrotCrate(state: GameState): GameState {
  const next = cloneState(state)
  for (const station of Object.values(next.stations)) {
    if (station.type !== 'crate' || station.dispenses !== 'carrot') continue
    station.type = 'wall'
    station.item = null
    station.container = null
    station.heldBy = null
    station.resumedAtTick = null
  }
  return next
}

/**
 * Key to action, shared by the component and by the tests that drive it.
 *
 * The bindings are `App.tsx`'s, deliberately: the tutorial exists so the keys a
 * player learns here are the keys the round wants — and now it is the same
 * object rather than a copy of it, because a copy is what let the round lose
 * Caps Lock while the tutorial kept it.
 */
export const SANDBOX_KEYS: Readonly<Record<string, Direction>> = KEY_TO_DIRECTION

/** Handy in tests: "walk left three, then press space" as a list of actions. */
export const move = (direction: Direction): PlayerAction => ({ kind: 'move', direction })
export const press: PlayerAction = { kind: 'interact' }
export const release: PlayerAction = { kind: 'interact_release' }
